import React, { useState } from 'react';
import { ApplicationContext, TranslationsContext, UserContext } from '@secretescapes/cauldron';
import {
  BreakpointProvider,
  Device,
  type IFalconTheme,
  ThemeProvider,
  falconTheme as defaultTheme,
  generateFalconTheme,
} from '@secretescapes/falcon';
import { type PageData } from 'src/server/server.interfaces';
import AppRouter from './App.router';
import { AppStateContext, type FinalRates } from './utils/AppStateContext';
import { ServerStateContext } from './utils/ServerStateContext';
import { mergePricingRules } from './utils/pricing.utils';

const App = (props: PageData) => {
  const {
    // App context props
    isSemiLoggedUser,
    isStaffUser,
    hasCredits,
    saleId,
    showPricingAndAvailability,
    showAds,
    warningMessageBody,
    previewMode,
    // Server context props
    sale,
    // Application context props
    affiliate,
    featureFlags,
    isMobile,
    isWhiteLabel,
    locale,
    territory,
    // User context props
    userId,
    // Translations context props
    translations = {},
    // Theme context props
    theme,
  } = props || {};

  const [finalRates, setFinalRates] = useState<FinalRates>({
    show: false,
    cheapestFinalRate: '',
    cheapestFinalRatePerRoomPerNight: '',
    flightsSelected: false,
    numberOfTravellers: 2,
    numberOfNights: 1,
  });

  const mergedPricingRules = mergePricingRules(
    sale?.prices?.pricingRules,
    showPricingAndAvailability
  );

  const falconTheme: IFalconTheme = theme ? generateFalconTheme(theme) : defaultTheme;

  return (
    <AppStateContext.Provider
      value={{
        saleId,
        pricingRules: mergedPricingRules,
        isSemiLoggedUser,
        isStaffUser,
        hasCredits,
        showPricingAndAvailability,
        showAds,
        warningMessageBody,
        finalRates,
        setFinalRates,
        previewMode,
      }}
    >
      <ServerStateContext.Provider value={{ sale }}>
        <ApplicationContext.Provider
          value={{
            territoryCode: territory,
            affiliateCode: affiliate,
            featureFlags: featureFlags ?? [],
            tracyFeatureFlags: [],
            userTerritoryCode: '',
            territoryLocale: locale,
            shortTerritoryLocale: locale.slice(0, 2),
            affiliateConfig: { isWhiteLabel },
            currency: sale?.prices?.currency?.currencyCode ?? 'GBP',
            isWhiteLabel,
            isMobile,
          }}
        >
          <UserContext.Provider value={{ userId }}>
            <TranslationsContext.Provider value={{ translations }}>
              <ThemeProvider theme={falconTheme}>
                <BreakpointProvider
                  value={isMobile ? Device.mobile : Device.desktop}
                  defaultValue={isMobile ? Device.mobile : Device.desktop}
                >
                  <AppRouter />
                </BreakpointProvider>
              </ThemeProvider>
            </TranslationsContext.Provider>
          </UserContext.Provider>
        </ApplicationContext.Provider>
      </ServerStateContext.Provider>
    </AppStateContext.Provider>
  );
};

export default App;
